import React, {useContext} from "react";
import {createUseStyles, useTheme} from "react-jss";
import Gauge from "./Gauge";
import LineChart, {LineChartData} from "./LineChart";
import QuestionSummary from "./QuestionSummary";
import {ProcessedResponse, TriviaState, TriviaStatus} from "../../state/state";
import Streak from "./Streak";
import AverageTime from "./AverageTime";
import {Theme} from "engage-client/theme";
import clsx from "clsx";
import {EngageAppContext} from "../../App";
import {Lobby, TeamMode, User} from "engage-common/types";

type DashboardProps = {
    className?: string
    hoveredUserID?: string
    leaderboardRowCount: number
}

export const GetLineChartData = (appState:TriviaState, lobby:Lobby|undefined, user:User|undefined, leaderboardRowCount:number) => {
    const lineChartData:LineChartData[] = appState.extra.leaderboard.reduce((array, entry, index) => {
        if(entry.userID !== user?.id && index < leaderboardRowCount) {
            array.push({
                userID: entry.userID,
                nickname: entry.nickname,
                teamID: entry.teamID,
                totals: [0].concat(entry.totals),
                currentPlayer: false
            });
        }
        return array;
    }, [] as LineChartData[]);
    
    if(lobby?.teamMode !== TeamMode.Off && lobby?.teams) {
        for(const team of lobby.teams) {
            const teamScore = appState.extra.teamScores[team.key];
            if(teamScore) {
                lineChartData.push({
                    userID: team.key,
                    teamID: team.key,
                    nickname: `Team - ${team.name}`,
                    totals: [0].concat(teamScore.totals),
                    currentPlayer: false
                });
            }
        }
    }
    return lineChartData;
};

export default function Dashboard(props:DashboardProps) {
    const {appState, userState, user, lobby} = useContext(EngageAppContext.current);

    let questionsAsked = appState.common.currentIndex;
    let pollQuestionsAsked = 0;
    for (let i = 0; i < appState.common.polls.length; i++) {
        if(appState.common.polls[i] < appState.common.currentIndex) {
            pollQuestionsAsked++;
        }
    }
    let scoredQuestionsAsked = questionsAsked - pollQuestionsAsked;

    if(appState.common.state === TriviaStatus.QuestionAnswer ||
                            appState.common.state === TriviaStatus.QuestionDetails) {
        //Once we've processed the leaderboard for that question we should count it.
        //Note that in the TriviaStatus.Complete state, index is already 1 greater so we don't need to add 1.
        questionsAsked++;
        if(!appState.common.currentQuestion?.poll) {
            scoredQuestionsAsked++;
        }
    }
    const lineChartData:LineChartData[] = GetLineChartData(appState, lobby, user,props.leaderboardRowCount);

    // Adding given user for individual dashboard
    if(user) {
        lineChartData.push({
            userID: user.id,
            teamID: user.team,
            nickname: user.nickname,
            totals: [0].concat(userState.scores.totals),
            currentPlayer: true
        });
    }
    const topScore = appState.extra.leaderboard.length ? appState.extra.leaderboard[0].total : 0;

    return <PureDashboard {...props} lineChartData={lineChartData} participantCount={appState.extra.participantCount}
                          topScore={topScore}
                          questionsAsked={questionsAsked} totalQuestions={appState.common.totalQuestions}
                          playerPosition={userState.position} playerResponses={userState.responses}
                          playerScore={userState.scores.total} playerStreak={userState.longestStreak}
                          hoveredUserID={props.hoveredUserID} scoredQuestionsAsked={scoredQuestionsAsked}/>;

}


const useStyles = createUseStyles<Theme>((theme) => ({
    dashboard: {
        flexGrow: 1,
        flexShrink: 0,
        background: theme.accessoryPanel.dashBoard.background,
        display:"flex",
        flexDirection: "column",
        gap: "10px",
        padding: "16px",
        [theme.breakpoints.up("lg")] : {
            overflowY: "auto"
        }
    },
    horizontalPanel: {
        display:"flex",
        flexGrow: 1,
        flexShrink: 0,
        [theme.breakpoints.down("md")] : {
            flexDirection: "column"
        },
        gap: "10px"
    },
    verticalPanel: {
        display: "flex",
        flexDirection: "column",
        flexGrow: 1,
        flexShrink: 0,
        gap: "5px"
    },
    largePanel: {
        flexGrow: 2,
        flexShrink: 1,
    }
}));

export type PureDashboardProps = DashboardProps & {
    playerScore: number
    playerPosition?: number
    topScore: number
    participantCount: number

    questionsAsked:number
    scoredQuestionsAsked:number

    lineChartData: LineChartData[]

    playerResponses: readonly (ProcessedResponse|undefined)[]
    totalQuestions: number

    playerStreak: number
}

export function PureDashboard(props:PureDashboardProps) {
    const classes = useStyles();
    const theme:Theme = useTheme();

    let playerCorrect = 0;
    let playerTime = 0;
    let answered = 0;
    for(let i = 0; i < props.playerResponses.length; i++) {
        let response = props.playerResponses[i];
        if(response) {
            answered++;
            playerTime += response.timeElapsed;
            if (response.score > 0 && i < props.questionsAsked) {
                playerCorrect++;
            }
        }
    }
    const averageTime = answered ? playerTime/answered : 0;
    const lineChartColumns = Math.min(props.totalQuestions, Math.max(props.questionsAsked+3, 5));

    return (
        <div className={clsx(classes.dashboard, props.className)}>
            <div className={classes.horizontalPanel}>
                <div className={classes.verticalPanel}>
                    <Gauge level={props.playerScore} max={props.topScore} position={props.playerPosition}
                           players={props.participantCount}/>
                </div>
                <LineChart className={classes.largePanel} data={props.lineChartData} totalQuestions={lineChartColumns}
                            hoveredUserID={props.hoveredUserID}/>
            </div>
            <div className={classes.horizontalPanel}>
                <QuestionSummary className={classes.largePanel} responses={props.playerResponses}
                                 totalQuestions={props.totalQuestions} questionsAsked={props.questionsAsked}
                                 correct={playerCorrect}/>
                <div className={classes.verticalPanel}>
                    <AverageTime averageTimeMS={averageTime} streak={props.playerStreak}/>
                </div>
            </div>
        </div>
    );
}